<!-- Toolbar -->
<p-toolbar class="p-mb-3">
  <!-- Left Group: Settings Management -->
  <div class="p-toolbar-group-left">
    <p-button
      label="New"
      icon="pi pi-file"
      class="p-button-secondary mr-3"
      (click)="startWithNewSettings()"
      pTooltip="Start with default advanced settings"
    ></p-button>
    <p-button
      label="Load"
      icon="pi pi-folder-open"
      class="p-button-primary mr-3"
      (click)="openLoadSettingsDialog()"
      pTooltip="Load saved advanced settings"
    ></p-button>
  </div>

  <!-- Center Group: Save Operation -->
  <div class="p-toolbar-group-center">
    <p-button
      label="Save"
      icon="pi pi-save"
      (click)="saveSettings()"
      [disabled]="!isModified"
      pTooltip="Save changes"
    ></p-button>
  </div>

  <!-- Right Group: Editing Tools -->
  <div class="p-toolbar-group-right">
    <p-button
      label="Reset"
      icon="pi pi-undo"
      class="p-button-warning mr-3"
      (click)="resetToOriginalSettings()"
      [disabled]="!isModified"
      pTooltip="Reset changes"
    ></p-button>
    <!-- Display Loaded Settings Info -->
    <span *ngIf="loadedSettingName" class="p-mr-2 loaded-settings">
      <i class="pi pi-cog"></i>
      {{ loadedSettingName }}
      <span *ngIf="loadedSettingVersion">(v{{ loadedSettingVersion }})</span>
    </span>
    <!-- Modified Indicator -->
    <span *ngIf="isModified" class="p-mr-2 modified-indicator">
      <i class="pi pi-info-circle"></i>
      Modified
    </span>
  </div>
</p-toolbar>

<!-- Accordion with Advanced Settings - Updated for PrimeNG 19+ -->
<p-accordion [multiple]="true" *ngIf="lendPeak">
  <!-- Term Configuration Section -->
  <p-accordion-panel value="termConfig">
    <p-accordion-header>Term Configuration</p-accordion-header>
    <p-accordion-content>
      <div class="p-fluid">
        <div class="field grid">
          <!-- Term Period Unit -->
          <div class="field col-6">
            <label for="termPeriodUnit">
              Term Period Unit
              <i class="pi pi-info-circle tooltip-icon" (click)="showTooltip($event, termPeriodUnitTooltip)"></i>
            </label>
            <p-select
              inputId="termPeriodUnit"
              [(ngModel)]="lendPeak.amortization.termPeriodDefinition.unit"
              [options]="termPeriodUnits"
              placeholder="Select a term period unit"
              (ngModelChange)="termPeriodDefinitionChange()"
              appendTo="body"
              class="w-full"
              [style]="{ width: '100%' }"
            ></p-select>
            <!-- Popover replacing OverlayPanel -->
            <p-popover #termPeriodUnitTooltip>
              <ng-template pTemplate="content">
                Defines the unit of time for each term period. For example, select "Month" for monthly payments.
              </ng-template>
            </p-popover>
          </div>

          <!-- Term Period Count -->
          <div class="field col-6">
            <label for="termPeriodCount">
              Term Period Count
              <i class="pi pi-info-circle tooltip-icon" (click)="showTooltip($event, termPeriodCountTooltip)"></i>
            </label>
            <p-inputNumber
              inputId="termPeriodCount"
              [(ngModel)]="lendPeak.amortization.termPeriodDefinition.count[0]"
              (ngModelChange)="termPeriodDefinitionChange()"
              class="w-full"
              inputStyleClass="w-full"
              [style]="{ width: '100%' }"
              [inputStyle]="{ width: '100%' }"
            ></p-inputNumber>
            <!-- Popover replacing OverlayPanel -->
            <p-popover #termPeriodCountTooltip>
              <ng-template pTemplate="content">
                Number of units per term period. For example, enter "1" for payments every 1 month.
              </ng-template>
            </p-popover>
          </div>

          <!-- Calendar Type -->
          <div class="field col-6">
            <label for="calendarType">
              Calendar Type
              <i class="pi pi-info-circle tooltip-icon" (click)="showTooltip($event, calendarTypeTooltip)"></i>
            </label>
            <p-select
              inputId="calendarType"
              [(ngModel)]="lendPeak.amortization.calendars.primary.calendarType"
              [options]="calendarTypes"
              placeholder="Select a calendar type"
              (ngModelChange)="onInputChange()"
              appendTo="body"
              class="w-full"
              [style]="{ width: '100%' }"
            ></p-select>
            <!-- Popover replacing OverlayPanel -->
            <p-popover #calendarTypeTooltip>
              <div class="p-mb-3">
                <strong>Example Parameters:</strong>
                <ul>
                  <li>Principal Amount: $10,000</li>
                  <li>Annual Interest Rate: 6%</li>
                </ul>
              </div>
              <p-table [value]="[{}]" tableStyleClass="p-datatable-gridlines">
                <ng-template pTemplate="header">
                  <tr>
                    <th>Method</th>
                    <th>Daily Interest Rate Formula</th>
                    <th>January (31 days)</th>
                    <th>February (28 days)</th>
                    <th>March (31 days)</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body">
                  <ng-container>
                    <tr>
                      <td>Actual/360</td>
                      <td>Annual Rate ÷ 360</td>
                      <td>$51.67</td>
                      <td>$46.67</td>
                      <td>$51.67</td>
                    </tr>
                    <tr>
                      <td>Actual/365</td>
                      <td>Annual Rate ÷ 365</td>
                      <td>$50.96</td>
                      <td>$46.03</td>
                      <td>$50.96</td>
                    </tr>
                    <tr>
                      <td>Actual/Actual</td>
                      <td>Annual Rate ÷ 365 (366 leap)</td>
                      <td>$50.96</td>
                      <td>$46.03</td>
                      <td>$50.96</td>
                    </tr>
                    <tr>
                      <td>30/360</td>
                      <td>Annual Rate ÷ 360</td>
                      <td>$50.00</td>
                      <td>$50.00</td>
                      <td>$50.00</td>
                    </tr>
                    <tr>
                      <td>30/Actual</td>
                      <td>Annual Rate ÷ Actual Year Days</td>
                      <td>$49.32</td>
                      <td>$49.32</td>
                      <td>$49.32</td>
                    </tr>
                  </ng-container>
                </ng-template>
              </p-table>
            </p-popover>
          </div>
        </div>
      </div>
    </p-accordion-content>
  </p-accordion-panel>

  <!-- Payment Application Settings Section -->
  <p-accordion-panel value="paymentApp">
    <p-accordion-header>Payment Application Settings</p-accordion-header>
    <p-accordion-content>
      <div class="p-fluid">
        <!-- Allocation Strategy -->
        <div class="field">
          <label for="paymentAllocationStrategy">
            Payment Allocation Strategy
            <i
              class="pi pi-info-circle tooltip-icon"
              (click)="showTooltip($event, paymentAllocationStrategyTooltip)"
            ></i>
          </label>
          <p-select
            inputId="paymentAllocationStrategy"
            [(ngModel)]="paymentAllocationStrategyName"
            [options]="paymentAllocationStrategies"
            placeholder="Select an allocation strategy"
            (ngModelChange)="onInputChange()"
            appendTo="body"
            class="w-full"
            [style]="{ width: '100%' }"
          ></p-select>
          <!-- Popover replacing OverlayPanel -->
          <p-popover #paymentAllocationStrategyTooltip>
            <ng-template pTemplate="content">
              <div class="tooltip-content">
                <p>Select the strategy for allocating deposits to bills...</p>
              </div>
            </ng-template>
          </p-popover>
        </div>

        <!-- Payment Priority -->
        <div class="field">
          <label for="paymentPriority">
            Payment Priority
            <i class="pi pi-info-circle tooltip-icon" (click)="showTooltip($event, paymentPriorityTooltip)"></i>
          </label>
          <div class="payment-priority-list w-full" style="max-width: 400px">
            <div *ngFor="let item of paymentPriority; let i = index" class="flex align-items-center gap-2 mb-2">
              <span class="flex-grow-1" [ngClass]="{ 'priority-hover': hoveredPriorityIndex === i }">
                {{ item | titlecase }}
              </span>
              <button
                pButton
                icon="pi pi-arrow-up"
                class="p-button-text p-button-sm"
                (click)="movePaymentPriorityUp(i)"
                [disabled]="i === 0"
                (mouseenter)="hoveredPriorityIndex = i"
                (mouseleave)="hoveredPriorityIndex = null"
              ></button>
              <button
                pButton
                icon="pi pi-arrow-down"
                class="p-button-text p-button-sm"
                (click)="movePaymentPriorityDown(i)"
                [disabled]="i === paymentPriority.length - 1"
                (mouseenter)="hoveredPriorityIndex = i"
                (mouseleave)="hoveredPriorityIndex = null"
              ></button>
            </div>
          </div>
          <p-popover #paymentPriorityTooltip>
            <ng-template pTemplate="content">
              <div class="tooltip-content">
                <p>Use the up/down arrows to reorder the order in which payments are applied to components.</p>
              </div>
            </ng-template>
          </p-popover>
        </div>
      </div>
    </p-accordion-content>
  </p-accordion-panel>

  <!-- Billing Settings Section -->
  <p-accordion-panel value="billingSettings">
    <p-accordion-header>Billing Settings</p-accordion-header>
    <p-accordion-content>
      <div class="p-fluid">
        <!-- Loan Type Selection -->
        <div class="field">
          <label for="billingModel">
            Loan Type
            <i class="pi pi-info-circle tooltip-icon" (click)="showTooltip($event, billingModelTooltip)"></i>
          </label>
          <p-select
            id="billingModel"
            [(ngModel)]="lendPeak.billingModel"
            name="billingModel"
            [options]="billingModelOptions"
            placeholder="Select a loan type"
            (ngModelChange)="onInputChange()"
            appendTo="body"
            class="w-full"
            [style]="{ width: '100%' }"
          ></p-select>
          <!-- Popover replacing OverlayPanel -->
          <p-popover #billingModelTooltip>
            <ng-template pTemplate="content">
              <div class="tooltip-content">
                <p>
                  <strong>Amortized Loan:</strong>
                  An amortized loan has scheduled payments applied to both principal and interest over the life of the
                  loan.
                </p>
                <p>
                  <strong>Daily Simple Interest Loan:</strong>
                  A daily simple interest loan accrues interest daily on the outstanding principal.
                </p>
              </div>
            </ng-template>
          </p-popover>
        </div>

        <!-- Billing Model Overrides -->
        <div class="field">
          <label for="billingModelOverrides">
            Billing Model Overrides
            <i class="pi pi-info-circle tooltip-icon" (click)="showTooltip($event, billingModelOverridesTooltip)"></i>
          </label>
          <p-popover #billingModelOverridesTooltip>
            <ng-template pTemplate="content">
              Override the billing model for specific terms. For example, you can switch from Amortized to DSI for
              certain terms.
            </ng-template>
          </p-popover>
          
          <!-- Container with visual boundaries -->
          <div class="billing-override-container mt-3 p-3 border-round">
            <!-- Header section -->
            <div class="flex align-items-center justify-content-between mb-3">
              <div class="flex align-items-center gap-2">
                <i class="pi pi-file-edit text-primary"></i>
                <span class="font-semibold text-color-secondary">Term-Specific Billing Model Changes</span>
              </div>
              <p-badge 
                [value]="(lendPeak.billingModelOverrides ? lendPeak.billingModelOverrides.length : 0).toString()" 
                [severity]="lendPeak.billingModelOverrides && lendPeak.billingModelOverrides.length > 0 ? 'success' : 'secondary'"
              ></p-badge>
            </div>
            
            <!-- Message when no overrides -->
            <p-message 
              *ngIf="!lendPeak.billingModelOverrides || lendPeak.billingModelOverrides.length === 0"
              severity="info"
              styleClass="mb-3"
              [closable]="false"
            >
              No billing model overrides configured. Click "Add Override" to specify term-specific billing model changes.
            </p-message>
            
            <!-- Inline editable table -->
            <p-table 
              *ngIf="lendPeak.billingModelOverrides && lendPeak.billingModelOverrides.length > 0"
              [value]="lendPeak.billingModelOverrides" 
              dataKey="term"
              editMode="row"
              class="p-datatable-sm"
              [style]="{'margin-bottom': '1rem'}"
            >
            <ng-template #header>
              <tr>
                <th style="width: 20%">Term</th>
                <th style="width: 25%">Period Start</th>
                <th style="width: 25%">Period End</th>
                <th style="width: 20%">Billing Model</th>
                <th style="width: 10%">Actions</th>
              </tr>
            </ng-template>
            <ng-template #body let-row let-editing="editing" let-ri="rowIndex">
              <tr [pEditableRow]="row">
                <!-- Term Number - editable -->
                <td pEditableColumn>
                  <p-cellEditor>
                    <ng-template #output>{{ row.term }}</ng-template>
                    <ng-template #input>
                      <p-inputNumber 
                        [(ngModel)]="row.term" 
                        [min]="0"
                        [showButtons]="true"
                        class="w-full"
                      ></p-inputNumber>
                    </ng-template>
                  </p-cellEditor>
                </td>
                
                <!-- Period Start - read only -->
                <td>{{ getTermStartDate(row.term) | date: 'MM/dd/yyyy' }}</td>
                
                <!-- Period End - read only -->
                <td>{{ getTermEndDate(row.term) | date: 'MM/dd/yyyy' }}</td>
                
                <!-- Billing Model - editable -->
                <td pEditableColumn>
                  <p-cellEditor>
                    <ng-template #output>
                      {{ row.model === 'dailySimpleInterest' ? 'Daily Simple Interest' : 'Amortized' }}
                    </ng-template>
                    <ng-template #input>
                      <p-select
                        [(ngModel)]="row.model"
                        [options]="billingModelOptions"
                        optionLabel="label"
                        optionValue="value"
                        appendTo="body"
                        class="w-full"
                      ></p-select>
                    </ng-template>
                  </p-cellEditor>
                </td>
                
                <!-- Actions -->
                <td class="text-center">
                  <div class="flex align-items-center justify-content-center gap-2">
                    <!-- view mode -->
                    <button
                      *ngIf="!editing"
                      pButton
                      pRipple
                      type="button"
                      pInitEditableRow
                      icon="pi pi-pencil"
                      (click)="onBmoEditInit(row)"
                      class="p-button-rounded p-button-text"
                      pTooltip="Edit row"
                    ></button>

                    <button
                      *ngIf="!editing"
                      pButton
                      pRipple
                      type="button"
                      icon="pi pi-trash"
                      (click)="removeBillingModelOverride(ri)"
                      class="p-button-rounded p-button-text p-button-danger"
                      pTooltip="Remove row"
                    ></button>

                    <!-- edit mode -->
                    <button
                      *ngIf="editing"
                      pButton
                      pRipple
                      type="button"
                      pSaveEditableRow
                      icon="pi pi-check"
                      (click)="onBmoEditSave(row)"
                      class="p-button-rounded p-button-text p-button-success"
                      pTooltip="Save"
                    ></button>

                    <button
                      *ngIf="editing"
                      pButton
                      pRipple
                      type="button"
                      pCancelEditableRow
                      icon="pi pi-times"
                      (click)="onBmoEditCancel(row, ri)"
                      class="p-button-rounded p-button-text"
                      pTooltip="Cancel"
                    ></button>
                  </div>
                </td>
              </tr>
            </ng-template>
            </p-table>
            
            <!-- Action buttons -->
            <div class="flex gap-2 pt-2 border-top-1 surface-border">
              <p-button
                label="Add Override"
                icon="pi pi-plus"
                (click)="addBillingModelOverrideRow()"
                class="p-button-sm"
                severity="primary"
              ></p-button>
              <p-button
                *ngIf="lendPeak.billingModelOverrides && lendPeak.billingModelOverrides.length > 0"
                label="Remove All"
                icon="pi pi-trash"
                (click)="removeAllBillingModelOverrides()"
                severity="danger"
                class="p-button-sm p-button-outlined"
              ></p-button>
            </div>
          </div>
        </div>

        <!-- Conditionally display these fields only for 'amortized' loans -->
        <div class="field grid">
          <!-- Default Pre Bill Days -->
          <div class="field col-6">
            <label for="defaultPreBillDaysConfiguration">
              Default Pre Bill Days
              <i class="pi pi-info-circle tooltip-icon" (click)="showTooltip($event, defaultPreBillDaysTooltip)"></i>
            </label>
            <p-inputNumber
              inputId="defaultPreBillDaysConfiguration"
              [(ngModel)]="lendPeak.amortization.jsDefaultPreBillDaysConfiguration"
              (ngModelChange)="onInputChange()"
              appendTo="body"
              class="w-full"
              inputStyleClass="w-full"
              [style]="{ width: '100%' }"
              [inputStyle]="{ width: '100%' }"
            ></p-inputNumber>
            <!-- Popover replacing OverlayPanel -->
            <p-popover #defaultPreBillDaysTooltip>
              <ng-template pTemplate="content">
                Number of days before the period end to open the bill by default.
              </ng-template>
            </p-popover>
          </div>

          <!-- Default Due Date Days -->
          <div class="field col-6">
            <label for="defaultBillDueDaysAfterPeriodEndConfiguration">
              Default Due Date Days
              <i class="pi pi-info-circle tooltip-icon" (click)="showTooltip($event, defaultDueDateDaysTooltip)"></i>
            </label>
            <p-inputNumber
              inputId="defaultBillDueDaysAfterPeriodEndConfiguration"
              [(ngModel)]="lendPeak.amortization.jsDefaultBillDueDaysAfterPeriodEndConfiguration"
              (ngModelChange)="onInputChange()"
              class="w-full"
              inputStyleClass="w-full"
              [style]="{ width: '100%' }"
              [inputStyle]="{ width: '100%' }"
            ></p-inputNumber>
            <!-- Popover replacing OverlayPanel -->
            <p-popover #defaultDueDateDaysTooltip>
              <ng-template pTemplate="content">
                Number of days after the period end when the payment is due by default.
              </ng-template>
            </p-popover>
          </div>
        </div>

        <!-- Auto-close Threshold -->
        <div class="field">
          <label for="autoCloseThreshold">
            Auto-close Threshold
            <i class="pi pi-info-circle tooltip-icon" (click)="showTooltip($event, autoCloseTooltip)"></i>
          </label>
          <p-inputNumber
            id="autoCloseThreshold"
            [(ngModel)]="lendPeak.jsAutoCloseThreshold"
            mode="currency"
            currency="USD"
            locale="en-US"
            (ngModelChange)="onInputChange()"
            class="w-full"
            inputStyleClass="w-full"
            [style]="{ width: '100%' }"
            [inputStyle]="{ width: '100%' }"
          ></p-inputNumber>
          <p-popover #autoCloseTooltip>
            <ng-template pTemplate="content">
              When the remaining payoff amount is &lt;= this value, the engine will generate a one-time
              <em>Auto Close</em>
              payment to bring the balance to&nbsp;zero.
            </ng-template>
          </p-popover>
        </div>
      </div>
    </p-accordion-content>
  </p-accordion-panel>

  <!-- Interest Settings Section -->
  <p-accordion-panel value="interestSettings">
    <p-accordion-header>Interest Settings</p-accordion-header>
    <p-accordion-content>
      <div class="p-fluid">
        <!-- Per Diem Calculation Type -->
        <div class="field">
          <label for="perDiemCalculationType">
            Per Diem Calculation Type
            <i class="pi pi-info-circle tooltip-icon" (click)="showTooltip($event, perDiemCalculationTypeTooltip)"></i>
          </label>
          <p-select
            id="perDiemCalculationType"
            [(ngModel)]="lendPeak.amortization.perDiemCalculationType"
            name="perDiemCalculationType"
            [options]="perDiemCalculationTypes"
            placeholder="Select a per diem calculation type"
            (ngModelChange)="onInputChange()"
            appendTo="body"
            class="w-full"
            [style]="{ width: '100%' }"
          ></p-select>
          <!-- Popover replacing OverlayPanel -->
          <p-popover #perDiemCalculationTypeTooltip>
            <ng-template pTemplate="content">
              <div class="tooltip-content">
                <p><strong>Per Diem Calculation Types:</strong></p>
                <ul>
                  <li>Annual Rate / Days in Year</li>
                  <li>Monthly Rate / Days in Month</li>
                </ul>
              </div>
            </ng-template>
          </p-popover>
        </div>

        <!-- Allow Rates Above 100% -->
        <div class="field-checkbox">
          <p-checkbox
            [(ngModel)]="lendPeak.amortization.allowRateAbove100"
            name="allowRateAbove100"
            binary="true"
            (ngModelChange)="onInputChange()"
          ></p-checkbox>
          <label for="allowRateAbove100">
            Allow rates above 100%
            <i class="pi pi-info-circle tooltip-icon" (click)="showTooltip($event, allowRateAbove100Tooltip)"></i>
          </label>
          <!-- Popover replacing OverlayPanel -->
          <p-popover #allowRateAbove100Tooltip>
            <ng-template pTemplate="content">
              Enable this option to allow setting interest rates higher than 100%.
            </ng-template>
          </p-popover>
        </div>

        <div class="field-checkbox">
          <p-checkbox
            [(ngModel)]="lendPeak.amortization.accrueInterestAfterEndDate"
            name="accrueInterestAfterEndDate"
            binary="true"
            (ngModelChange)="onInputChange()"
          ></p-checkbox>
          <label for="accrueInterestAfterEndDate">
            Accrue Interest After End Date
            <i
              class="pi pi-info-circle tooltip-icon"
              (click)="showTooltip($event, accrueInterestAfterEndDateTooltip)"
            ></i>
          </label>
          <!-- Popover replacing OverlayPanel -->
          <p-popover #accrueInterestAfterEndDateTooltip>
            <ng-template pTemplate="content">
              If Bill remains unpaid after the end date, interest will continue to accrue on owed principal
            </ng-template>
          </p-popover>
        </div>

        <div class="field-checkbox">
          <p-checkbox
            [(ngModel)]="lendPeak.amortization.interestAccruesFromDayZero"
            name="interestAccruesFromDayZero"
            binary="true"
            (ngModelChange)="onInputChange()"
          ></p-checkbox>
          <label for="interestAccruesFromDayZero">
            Accrue Interest&nbsp;From&nbsp;Day&nbsp;0
            <i
              class="pi pi-info-circle tooltip-icon"
              (click)="showTooltip($event, interestAccruesFromDayZeroTooltip)"
            ></i>
          </label>

          <!-- Pop-over tooltip -->
          <p-popover #interestAccruesFromDayZeroTooltip>
            <ng-template pTemplate="content">
              <p>
                When enabled, interest for a given day is recognised
                <strong>at the very start of the day</strong>
                (“day-0”).&nbsp; When disabled (default), interest is not due until the first second of the
                <em>next</em>
                day (“day-1” behaviour).
              </p>
              <p class="p-mt-1">
                The flag only affects partial-period pay-offs and snapshot accruals; it does not change your regular
                term interest.
              </p>
            </ng-template>
          </p-popover>
        </div>

        <!-- Unbilled Interest Flush Method -->
        <div class="field">
          <label for="flushMethod">
            Unbilled Interest Flush Method
            <i class="pi pi-info-circle tooltip-icon" (click)="showTooltip($event, flushMethodTooltip)"></i>
          </label>
          <p-select
            id="flushMethod"
            [(ngModel)]="lendPeak.amortization.flushUnbilledInterestRoundingErrorMethod"
            name="flushMethod"
            [options]="flushMethods"
            placeholder="Select a flush method"
            (ngModelChange)="onInputChange()"
            appendTo="body"
            class="w-full"
            [style]="{ width: '100%' }"
          ></p-select>
          <!-- Popover replacing OverlayPanel -->
          <p-popover #flushMethodTooltip>
            <ng-template pTemplate="content">
              Determines how unbilled interest is handled. Choose a method to define when unbilled interest should be
              flushed.
            </ng-template>
          </p-popover>
        </div>

        <!-- Flush Threshold -->
        <div class="field" *ngIf="lendPeak.amortization.flushUnbilledInterestRoundingErrorMethod !== 'none'">
          <label for="flushThreshold">
            Unbilled Interest Flush Threshold
            <i class="pi pi-info-circle tooltip-icon" (click)="showTooltip($event, flushThresholdTooltip)"></i>
          </label>
          <p-inputNumber
            id="flushThreshold"
            [(ngModel)]="lendPeak.amortization.jsFlushThreshold"
            name="flushThreshold"
            mode="currency"
            currency="USD"
            locale="en-US"
            (ngModelChange)="onInputChange()"
            class="w-full"
            inputStyleClass="w-full"
            [style]="{ width: '100%' }"
            [inputStyle]="{ width: '100%' }"
          ></p-inputNumber>
          <!-- Popover replacing OverlayPanel -->
          <p-popover #flushThresholdTooltip>
            <ng-template pTemplate="content">
              The threshold amount for flushing unbilled interest. For example, enter "$0.01" to flush unbilled interest
              when it reaches one cent.
            </ng-template>
          </p-popover>
        </div>
      </div>
    </p-accordion-content>
  </p-accordion-panel>

  <!-- Rounding Settings Section -->
  <p-accordion-panel value="roundingSettings">
    <p-accordion-header>Rounding Settings</p-accordion-header>
    <p-accordion-content>
      <div class="p-fluid">
        <!-- Rounding Method -->
        <div class="field">
          <label for="roundingMethod">
            Rounding Method
            <i class="pi pi-info-circle tooltip-icon" (click)="showTooltip($event, roundingMethodTooltip)"></i>
          </label>
          <p-select
            id="roundingMethod"
            [(ngModel)]="lendPeak.amortization.roundingMethod"
            name="roundingMethod"
            [options]="roundingMethods"
            placeholder="Select a rounding method"
            (ngModelChange)="onInputChange()"
            appendTo="body"
            class="w-full"
            [style]="{ width: '100%' }"
          ></p-select>
          <!-- Popover replacing OverlayPanel -->
          <p-popover #roundingMethodTooltip>
            <div class="p-mt-5 p-card p-shadow-2">
              <div class="p-card-body">
                <strong>Rounding Options:</strong>
                <ul class="p-mt-2">
                  <li>
                    <strong>Round Up:</strong>
                    Rounds up to the nearest specified precision.
                    <ul>
                      <li>
                        2.301 →
                        <strong>2.31</strong>
                      </li>
                      <li>
                        2.374 →
                        <strong>2.38</strong>
                      </li>
                      <li>
                        -2.301 →
                        <strong>-2.30</strong>
                      </li>
                    </ul>
                  </li>
                  <li>
                    <strong>Round Down:</strong>
                    Rounds down to the nearest specified precision.
                    <ul>
                      <li>
                        2.399 →
                        <strong>2.39</strong>
                      </li>
                      <li>
                        2.376 →
                        <strong>2.37</strong>
                      </li>
                      <li>
                        -2.399 →
                        <strong>-2.40</strong>
                      </li>
                    </ul>
                  </li>
                  <li>
                    <strong>Round Half Up:</strong>
                    Rounds to nearest neighbor; rounds away from zero if equidistant.
                    <ul>
                      <li>
                        2.345 →
                        <strong>2.35</strong>
                      </li>
                      <li>
                        2.344 →
                        <strong>2.34</strong>
                      </li>
                      <li>
                        -2.345 →
                        <strong>-2.35</strong>
                      </li>
                    </ul>
                  </li>
                  <li>
                    <strong>Round Half Down:</strong>
                    Rounds to nearest neighbor; rounds towards zero if equidistant.
                    <ul>
                      <li>
                        2.345 →
                        <strong>2.34</strong>
                      </li>
                      <li>
                        2.346 →
                        <strong>2.35</strong>
                      </li>
                      <li>
                        -2.345 →
                        <strong>-2.34</strong>
                      </li>
                    </ul>
                  </li>
                  <li>
                    <strong>Round Half Even (Bankers' Rounding):</strong>
                    Rounds to nearest neighbor; rounds towards nearest even number if equidistant.
                    <ul>
                      <li>
                        2.345 →
                        <strong>2.34</strong>
                        (4 is even)
                      </li>
                      <li>
                        2.355 →
                        <strong>2.36</strong>
                        (6 is even)
                      </li>
                      <li>
                        -2.345 →
                        <strong>-2.34</strong>
                      </li>
                    </ul>
                  </li>
                </ul>
              </div>
            </div>
          </p-popover>
        </div>

        <!-- Rounding Precision -->
        <div class="field">
          <label for="roundingPrecision">
            Rounding Precision (decimal places)
            <i class="pi pi-info-circle tooltip-icon" (click)="showTooltip($event, roundingPrecisionTooltip)"></i>
          </label>
          <p-inputNumber
            id="roundingPrecision"
            [(ngModel)]="lendPeak.amortization.roundingPrecision"
            name="roundingPrecision"
            (ngModelChange)="onInputChange()"
            class="w-full"
            inputStyleClass="w-full"
            [style]="{ width: '100%' }"
            [inputStyle]="{ width: '100%' }"
          ></p-inputNumber>
          <!-- Popover replacing OverlayPanel -->
          <p-popover #roundingPrecisionTooltip>
            <ng-template pTemplate="content">
              Number of decimal places to round to. For example, enter "2" to round amounts to two decimal places.
            </ng-template>
          </p-popover>
        </div>
      </div>
    </p-accordion-content>
  </p-accordion-panel>
</p-accordion>

<!-- Save Settings Dialog -->
<p-dialog
  header="Save Advanced Settings"
  [(visible)]="showSaveSettingsDialog"
  [modal]="true"
  [closable]="true"
  [style]="{ width: '400px' }"
>
  <div class="p-fluid">
    <div class="field">
      <label for="settingName">Setting Name</label>
      <input id="settingName" type="text" pInputText [(ngModel)]="newSetting.name" />
    </div>
    <div class="field-checkbox">
      <p-checkbox [(ngModel)]="newSetting.isDefault" binary="true"></p-checkbox>
      <label for="isDefault">Set as default</label>
    </div>
  </div>
  <div class="flex gap-2 mt-4 justify-content-end">
    <div class="p-d-flex p-jc-end">
      <p-button label="Cancel" class="p-mr-2" (onClick)="showSaveSettingsDialog = false"></p-button>
      <p-button label="Save" (onClick)="confirmSaveSettings()"></p-button>
    </div>
  </div>
</p-dialog>

<!-- Load Settings Dialog -->
<p-dialog
  header="Load Advanced Settings"
  [(visible)]="showLoadSettingsDialog"
  [modal]="true"
  [style]="{ width: '600px' }"
  [closable]="true"
>
  <p-table [value]="savedSettings" selectionMode="single" [(selection)]="selectedSetting" [paginator]="true" [rows]="5">
    <ng-template pTemplate="header">
      <tr>
        <th>Name</th>
        <th>Version</th>
        <th>Created At</th>
        <th>Actions</th>
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-setting>
      <tr [pSelectableRow]="setting">
        <td>{{ setting.name }}</td>
        <td>{{ setting.version }}</td>
        <td>{{ setting.createdAt | date: 'short' }}</td>
        <td>
          <div class="p-d-flex p-ai-center">
            <p-button
              icon="pi pi-eye"
              class="p-mr-2 pr-2"
              severity="info"
              (onClick)="previewSettings(setting)"
              pTooltip="Preview Settings"
            ></p-button>

            <p-button
              icon="pi pi-trash"
              severity="danger"
              (onClick)="deleteSetting(setting.id)"
              pTooltip="Delete Settings"
            ></p-button>
          </div>
        </td>
      </tr>
    </ng-template>
  </p-table>
  <div class="flex gap-2 mt-4 justify-content-end">
    <div class="p-d-flex p-jc-end">
      <p-button label="Cancel" class="p-mr-2 pr-2" (onClick)="showLoadSettingsDialog = false"></p-button>
      <p-button label="Load" [disabled]="!selectedSetting" (onClick)="confirmLoadSettings()"></p-button>
    </div>
  </div>
</p-dialog>

<!-- Preview Settings Dialog -->
<p-dialog
  header="Settings Preview"
  [(visible)]="showPreviewSettingsDialog"
  [modal]="true"
  [closable]="true"
  [style]="{ width: '600px' }"
>
  <pre>{{ selectedSetting?.settings | json }}</pre>
  <div class="flex gap-2 mt-4 justify-content-end">
    <button pButton label="Close" (click)="showPreviewSettingsDialog = false"></button>
  </div>
</p-dialog>

<!-- Dialog removed - now using inline editing -->
